import axios from 'axios';
import { isOriginalMenus, OriginalMenu } from 'types/menu';
import {
  MenuCategory,
  isOriginalMenuCategories,
  mapOriginalMenuCategoryToMenuCategory,
} from 'types/menuCategory';
import { createErr, createOk, Result } from 'types/result';
import { constructUrl } from './utils';

/**
 * メニューカテゴリー新規登録 payload
 */
export type MenuCategoryRegistrationPayload = {
  /** メニューカテゴリーID */
  id: number | null;
  /** メニューカテゴリー名 */
  name: string;
  /** 並び順 */
  displayOrder: number;
};

/**
 * メニューカテゴリー編集 payload
 */
export type MenuCategoryEditPayload = {
  /** メニューカテゴリー名 */
  name: string;
};

/**
 * メニューカテゴリーの順序の並べ替え payload
 */
export type MenuCategoryOrderPayload = {
  id: number;
  displayOrder: number;
}[];

/**
 * 特定のメニューカテゴリーに紐づいたメニューの更新 payload
 */
type MenusRelatedToCategoryPayload = {
  categoryId: number;
  id: number;
  name: string;
  type: string;
  description: string;
  displayOrder: number;
}[];

const URL = '/v1/menu-categories';

/**
 * メニューカテゴリー一覧を取得する
 * @returns メニューカテゴリー一覧
 */
export const fetchMenuCategories = async (): Promise<
  Result<MenuCategory[], Error>
> => {
  const url = constructUrl(URL);

  return axios
    .get(url)
    .then((res) => {
      const data = res.data as unknown;

      if (!isOriginalMenuCategories(data)) {
        throw Error(`Unsupported Value: ${JSON.stringify(data)}`);
      }

      return createOk(data.map(mapOriginalMenuCategoryToMenuCategory));
    })
    .catch((err: Error) => createErr(err));
};

/**
 * メニューカテゴリーを新規登録する
 */
export const addMenuCategory = async (
  payload: MenuCategoryRegistrationPayload,
): Promise<Result<MenuCategory, Error>> =>
  axios
    .post(constructUrl(URL), payload)
    .then(({ data }) => createOk(data))
    .catch((err: Error) => createErr(err));

/**
 * メニューカテゴリーの順序の並べ替え
 */
export const editMenuCategoryOrder = async (
  payload: MenuCategoryOrderPayload,
): Promise<Result<MenuCategory[], Error>> =>
  axios
    .put(constructUrl(URL), payload)
    .then(({ data }) => createOk(data))
    .catch((err: Error) => createErr(err));

/**
 * メニューカテゴリーを更新する
 */
export const editMenuCategory = async (
  categoryId: number,
  payload: MenuCategoryEditPayload,
): Promise<Result<MenuCategory, Error>> => {
  // TODO: エンドポイント直り次第フロントエンドも修正する
  // const url = constructUrl(`${URL}/${categoryId}`);
  const url = constructUrl(`/v1/menu-category/${categoryId}`);

  return axios
    .put(url, payload)
    .then(({ data }) => createOk(data))
    .catch((err: Error) => createErr(err));
};

/**
 * メニューカテゴリーを削除する
 */
export const deleteMenuCategory = async (
  categoryId: MenuCategory['id'],
): Promise<Result<null, Error>> => {
  // TODO: エンドポイント直り次第フロントエンドも修正する
  // const url = constructUrl(`${URL}/${categoryId}`);
  const url = constructUrl(`/v1/menu-category/${categoryId}`);

  return axios
    .delete(url)
    .then(() => createOk(null))
    .catch((err: Error) => createErr(err));
};

/**
 * 特定のメニューカテゴリーに紐づいたメニュー一覧を取得する
 */
export const fetchMenusRelatedToCategory = (
  categoryId: number,
): Promise<Result<OriginalMenu[], Error>> => {
  // TODO: エンドポイント直り次第フロントエンドも修正する
  // const url = constructUrl(`${URL}/${categoryId}/menus`);
  const url = constructUrl(`/v1/menu-category/${categoryId}/menus`);

  return axios
    .get(url)
    .then((res) => {
      const data = res.data as unknown;

      if (!isOriginalMenus(data)) {
        throw Error(`Unsupported Value: ${JSON.stringify(data)}`);
      }

      return createOk(data);
    })
    .catch((err: Error) => createErr(err));
};

/**
 * 特定のメニューカテゴリーに紐づいたメニューを更新する
 */
export const editMenusRelatedToCategory = (
  categoryId: number,
  payload: MenusRelatedToCategoryPayload,
): Promise<Result<OriginalMenu[], Error>> => {
  // TODO: エンドポイント直り次第フロントエンドも修正する
  // const url = constructUrl(`${URL}/${categoryId}/menus`);
  const url = constructUrl(`/v1/menu-category/${categoryId}/menus`);

  return axios
    .put(url, payload)
    .then((res) => {
      const data = res.data as unknown;

      if (!isOriginalMenus(data)) {
        throw Error(`Unsupported Value: ${JSON.stringify(data)}`);
      }

      return createOk(data);
    })
    .catch((err: Error) => createErr(err));
};
